import React from 'react';
import { Link } from 'react-router-dom';
import Seo from '../../components/Seo';
import FaqSection from '../../components/Faq';
import { useTheme } from '../../theme';
import { articleSchema, faqPageSchema, breadcrumbSchema, Faq } from '../../lib/schema';

const PATH = '/base64-encoding-errors';

const FAQS: Faq[] = [
  { q: '"Invalid character" — what does this mean?', a: 'Your string contains a character outside the Base64 alphabet (A-Z, a-z, 0-9, "+", "/", "=" padding) — commonly caused by copy-paste adding smart quotes, or mixing up standard and URL-safe Base64.' },
  { q: '"Invalid padding" or "incorrect padding" — what does this mean?', a: 'Standard Base64 output length must always be a multiple of 4, using "=" characters to pad the final group. This error means characters were dropped (often a trailing "=" trimmed by a text editor) or the string was truncated.' },
  { q: 'Why does my Base64 string work in one language but not another?', a: 'Usually a standard-vs-URL-safe mismatch: one side encoded with "+"/"/" and the other expects "-"/"_" (or vice versa), or one library expects padding and the other omits it.' },
  { q: 'How can I check if a string is valid before decoding it?', a: 'Use the Base64 Validator tool, which reports the exact reason a string is invalid rather than just failing silently.' },
];

export default function Base64EncodingErrorsPage() {
  const { themeClasses } = useTheme();
  const heading = `text-2xl font-bold ${themeClasses.textPrimary} mt-10 mb-4`;
  const para = `${themeClasses.textSecondary} leading-relaxed mb-4`;

  return (
    <>
      <Seo
        title="Common Base64 Errors and How to Fix Them | EasyBase64"
        description="Fix the most common Base64 decoding errors: invalid character, invalid padding, and standard vs. URL-safe mismatches — with clear explanations and examples."
        path={PATH}
        jsonLd={[
          articleSchema({
            headline: 'Common Base64 Errors and How to Fix Them',
            description: 'A troubleshooting guide for the most common Base64 encode/decode errors.',
            path: PATH,
            datePublished: '2026-07-07',
            dateModified: '2026-07-07',
          }),
          faqPageSchema(FAQS),
          breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'Base64 Encoding Errors', path: PATH }]),
        ]}
      />

      <div className="max-w-3xl mx-auto">
        <article className={`${themeClasses.infoCard} rounded-2xl p-6 sm:p-8 md:p-12 border mb-12`}>
          <h1 className={`text-4xl font-bold tracking-tight ${themeClasses.textPrimary} mb-6`}>
            Common Base64 Errors and How to Fix Them
          </h1>

          <p className={para}>
            Almost every "Base64 decode failed" error falls into one of a small number of
            categories. Here's how to diagnose and fix each one, and how to check a string before
            you even try to decode it.
          </p>

          <h2 className={heading}>"Invalid character" errors</h2>
          <p className={para}>
            Standard Base64 only permits <code>A-Z</code>, <code>a-z</code>, <code>0-9</code>,{' '}
            <code>+</code>, <code>/</code>, and <code>=</code> for padding. This error usually
            means the string picked up something outside that set along the way — smart quotes or
            en-dashes introduced by copying from a word processor, stray whitespace pasted in the
            middle of the string, or a URL-safe string (using <code>-</code>/<code>_</code>) being
            fed to a decoder that expects the standard alphabet. Check which variant you actually
            have with the <Link to="/base64-validator" className="underline">Base64 Validator</Link>.
          </p>

          <h2 className={heading}>"Invalid padding" / "incorrect padding" errors</h2>
          <p className={para}>
            Standard Base64's output length must always be a multiple of 4 — the encoder adds one
            or two trailing <code>=</code> characters to guarantee this. This error means the
            string you're decoding isn't a multiple of 4 characters, most often because a
            trailing <code>=</code> was accidentally trimmed (some text editors or "clean up
            whitespace" tools strip it), or the string was truncated when copied. The fix is
            usually to re-copy the full string, or, if you know it's URL-safe Base64, re-pad it
            before decoding standard Base64.
          </p>

          <h2 className={heading}>Standard vs. URL-safe mismatches</h2>
          <p className={para}>
            A string encoded with <code>+</code>/<code>/</code> will fail (or silently produce
            garbage) if decoded as URL-safe, and vice versa. This is especially common with JWTs
            and tokens, which specifically use{' '}
            <Link to="/url-safe-base64" className="underline">URL-safe Base64</Link>. If a value
            came from a URL, cookie, or filename, try the URL-safe tool; if it came from a JSON
            payload or raw API response, try standard Base64.
          </p>

          <h2 className={heading}>Line breaks in the middle of a value</h2>
          <p className={para}>
            MIME/email Base64 wraps lines at 76 characters (see{' '}
            <Link to="/base64-mime-encode" className="underline">MIME Base64</Link>), which will
            confuse a decoder expecting a single unbroken line. Strip line breaks before decoding,
            or use a decoder built to handle wrapped MIME text.
          </p>

          <h2 className={heading}>Check before you decode</h2>
          <p className={para}>
            Rather than guessing from an error message, run the string through the{' '}
            <Link to="/base64-validator" className="underline">Base64 Validator</Link> first — it
            reports the specific reason a string fails (wrong alphabet, bad padding, wrong length)
            instead of a generic decode error.
          </p>
        </article>

        <FaqSection title="Base64 Encoding Errors — FAQ" faqs={FAQS} />
      </div>
    </>
  );
}
